import { create } from 'zustand'
import {
    storageKeyFor,
    writeNarrationStartedAt,
    clearNarrationStartedAt,
} from './onboardingStorage'
import {
    normalizeStage,
    getStageFlow,
    getStepForStage,
    BRING_SOURCE_KEYS,
    STAGES,
} from '../components/OnboardingGuide/copy'
import {
    trackOnboarding,
    markPersona,
} from '../components/OnboardingGuide/trackOnboarding'

/**
 * Observed real-action signals. They latch, so an action taken before its stage
 * opens still counts. Session-only.
 */
export const GUIDE_SIGNALS = {
    TOPIC_SELECTED: 'topicSelected',
    SUMMARY_REQUESTED: 'summaryRequested',
    GAPS_VIEW_OPENED: 'gapsViewOpened',
    GAP_HINT_OPENED: 'gapHintOpened',
    GAP_BRIDGE_REQUESTED: 'gapBridgeRequested',
    SAVED_TO_NOTES: 'savedToNotes',
    STATEMENTS_ADDED: 'statementsAdded',
    AGENT_GRAPH_APPEARED: 'agentGraphAppeared',
}

/**
 * State for the first-session onboarding guide.
 *
 * status: 'unknown' (not resolved yet) | 'active' | 'ineligible' (has graphs
 * and an account older than a week; re-derived every load, never persisted) |
 * 'dismissed' | 'done'. `startedWithKey` says an API key already existed when
 * the guide started, so that key can't count as finishing the loop. `stage` follows copy.js getStageFlow(persona)
 * while active. `persona` is the profile's answer to the persona question
 * (PersonaToast); `personaAsked` says the question was settled (answered, or
 * unanswerable this session), so the guide knows whether to wait for it.
 * `upgradeReminderOpen` holds the guide back while the upgrade reminder that
 * follows a pick is on screen. Persisted per username
 * (`onboardingGuide.v1:<username>`) so accounts sharing a browser never inherit
 * each other's verdict; nothing is persisted before hydrateForUser() names the
 * viewer.
 */

const readPersisted = username => {
    try {
        const raw = localStorage.getItem(storageKeyFor(username))
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

const writePersisted = state => {
    if (!state.storageUsername) return
    try {
        localStorage.setItem(
            storageKeyFor(state.storageUsername),
            JSON.stringify({
                status: state.status,
                stage: state.stage,
                persona: state.persona,
                personaAsked: state.personaAsked,
                startedWithKey: state.startedWithKey,
            })
        )
    } catch {}
}

export const useOnboardingStore = create((set, get) => ({
    status: 'unknown',
    stage: 'bring',
    persona: null,
    personaAsked: false,
    startedWithKey: false,
    collapsed: false,
    storageUsername: null,
    importNarrationActive: false,
    signals: {},
    // Bumped when the AI widget settles a request; compared against the value at request
    // time so the widget's own auto-fired summary can't advance a stage.
    aiResponseTick: 0,
    // As reported by ApiTokenManager; null until it says, and never guessed.
    apiKeyCount: null,
    // Tells "finishing the loop now" from "arriving with it already finished".
    advancedThisSession: false,
    upgradeReminderOpen: false,
    // After the boot narration, the guide stays out of sight until this time so
    // the user meets their graph before being guided. Session-only.
    introHoldUntil: null,
    // "The overlay just came off." The graph's one-shot intro swing fires when
    // its layout settles, which under the narration happens out of sight; the
    // graph takes this counter as `orbitTrigger` and swings when it changes
    // instead. A counter, not a flag: it is an event, and React reacts to the
    // change.
    graphRevealTick: 0,

    hydrateForUser: username => {
        if (!username || get().storageUsername === username) return
        const persisted = readPersisted(username)
        // Older builds stored 'ineligible' for good; it is decided afresh now.
        const status =
            persisted.status && persisted.status !== 'ineligible'
                ? persisted.status
                : 'unknown'
        set({
            storageUsername: username,
            status,
            stage: normalizeStage(persisted.stage || 'bring'),
            persona: persisted.persona || null,
            personaAsked: !!persisted.personaAsked,
            startedWithKey: !!persisted.startedWithKey,
            collapsed: false,
            signals: {},
            advancedThisSession: false,
        })
    },

    recordSignal: name => {
        if (!name || get().signals[name]) return
        set({ signals: { ...get().signals, [name]: true } })
    },

    recordAiResponse: () => set({ aiResponseTick: get().aiResponseTick + 1 }),

    clearSignal: name => {
        if (!name || !get().signals[name]) return
        const { [name]: removed, ...rest } = get().signals
        set({ signals: rest })
    },

    // A persona answered before activation (the question shows to everyone)
    // already names the first stage.
    activate: ({ startedWithKey = false } = {}) => {
        set({
            status: 'active',
            stage: getStageFlow(get().persona)[0],
            startedWithKey,
        })
        writePersisted(get())
    },

    // The persona question's answer: a code, or null when the profile could
    // not be read. The guide's first stage follows the persona's flow, so it
    // is reset here while the loop hasn't started; `source` says who answered
    // (the toast, or a profile that already had it). `persist: false` keeps an
    // unanswerable question (profile unavailable) open for the next visit.
    setPersona: (
        persona,
        { source = 'picked', prefilled = false, persist = true } = {}
    ) => {
        const state = get()
        const firstStage = getStageFlow(persona)[0]
        const loopNotStarted =
            state.status === 'active' &&
            !state.advancedThisSession &&
            (state.stage === STAGES.BRING || state.stage === STAGES.CONNECT)
        set({
            persona: persona || null,
            personaAsked: true,
            ...(loopNotStarted ? { stage: firstStage } : {}),
        })
        if (persist) writePersisted(get())
        if (state.status === 'active') {
            markPersona(persona)
            trackOnboarding('persona_selected', {
                persona: persona || 'none',
                source,
                prefilled,
            })
        }
    },

    markIneligible: () => set({ status: 'ineligible' }),

    dismiss: () => {
        // Closing on the final stage counts as finishing the loop.
        set({
            status: get().stage === STAGES.EXPLORE ? 'done' : 'dismissed',
        })
        writePersisted(get())
    },

    // What moved the stage — observed, latched, an AI answer, or the fallback timer —
    // so a timeout never counts as user progress.
    advanceTo: (nextStage, { advancedBy = 'unknown' } = {}) => {
        const completedStage = get().stage
        set({ stage: nextStage, collapsed: false, advancedThisSession: true })
        writePersisted(get())
        trackOnboarding('step_completed', {
            stage: completedStage,
            advanced_by: advancedBy,
            step: getStepForStage(completedStage, get().persona),
            persona: get().persona || 'none',
        })
    },

    complete: () => {
        set({ status: 'done' })
        writePersisted(get())
    },

    setApiKeyCount: count => set({ apiKeyCount: count }),

    setCollapsed: collapsed => set({ collapsed: !!collapsed }),

    // Opened before setPersona releases the guide, so its first toast never
    // shows under the reminder.
    openUpgradeReminder: () => set({ upgradeReminderOpen: true }),

    closeUpgradeReminder: () => set({ upgradeReminderOpen: false }),

    holdIntro: durationMs => set({ introHoldUntil: Date.now() + durationMs }),

    revealGraph: () => set({ graphRevealTick: get().graphRevealTick + 1 }),

    // Set by ImportingNotice when an import POST goes out. Never persisted.
    startImportNarration: () => {
        writeNarrationStartedAt(Date.now())
        set({ importNarrationActive: true })
    },

    cancelImportNarration: () => {
        clearNarrationStartedAt()
        set({ importNarrationActive: false })
    },
}))

/**
 * The one positive read of the guide's status: a failed contexts fetch leaves it
 * 'unknown' forever, and every guided-only behaviour must degrade to the normal app.
 */
export const selectGuideIsActive = state => state.status === 'active'

/**
 * The persona question (PersonaToast) goes before the guide's first toast: a
 * guided user at the start of the loop who hasn't answered it yet. Both
 * components read this so they agree on the moment.
 */
export const selectGuideWaitsForPersona = state =>
    selectGuideIsActive(state) &&
    !state.personaAsked &&
    !state.advancedThisSession &&
    state.stage === STAGES.BRING

/**
 * Import dialogs collapse to their input only on the `bring` stage — the one stage
 * whose asked action is an import. `status` alone stays 'active' far past it.
 */
export const selectGuideSimplifiesImports = state =>
    selectGuideIsActive(state) && state.stage === STAGES.BRING

/**
 * The /apps source keys the persona's first step is about, or null when the
 * guide isn't asking for an import (every source shows) or the persona has no
 * list of its own (the caller's no-setup default applies).
 */
export const selectGuideBringSourceKeys = state =>
    selectGuideSimplifiesImports(state)
        ? BRING_SOURCE_KEYS[state.persona] || null
        : null

/** Before the user's first AI request; `summarize` advances on nothing else. */
const STAGES_BEFORE_FIRST_AI_REQUEST = [
    STAGES.CONNECT,
    STAGES.BRING,
    STAGES.TOPICS,
    STAGES.SUMMARIZE,
]

/**
 * Skip the AI panel's unrequested auto-open (AIChat/index.jsx) until the user's
 * own first AI request. Gated positively on 'active', see selectGuideIsActive.
 */
export const selectGuideSuppressesAiAutoOpen = state =>
    selectGuideIsActive(state) &&
    STAGES_BEFORE_FIRST_AI_REQUEST.includes(state.stage)
